import * as trace from 'owa-trace';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import getTableViewConversationRelation from 'mini-mail-list-store/lib/utils/getTableViewRowRelation';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import Item from 'owa-service/lib/contract/Item';
import MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import TableQuery from 'mini-mail-list-store/lib/store/schema/TableQuery';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import TableViewConversationRelation from 'mini-mail-list-store/lib/store/schema/TableViewConversationRelation';
import { getConversationOrItemIdFromRow } from 'mini-mail-list-table-operations/lib/utils/getConversationOrItemIdFromRow';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';
import { shouldTableSortByRenewTime } from '../../utils/shouldFolderSortByRenewTime';
import { SortColumn } from 'mini-common-schema/lib/schema/SortBy';
import { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';

export default function isSortKeyEqual(row: MailListRowDataType, tableView: TableView) {
    const { tableQuery } = tableView;
    const rowId = getConversationOrItemIdFromRow(row);

    const sortBy = (tableQuery as MailFolderTableQuery).sortBy;
    if (!sortBy) {
        return false;
    }

    if (!(tableQuery.type === TableQueryType.Folder)) {
        trace.errorThatWillCauseAlert(
            'isRowSortKeyEqual should not be called on non MailFolderTableQuery type'
        );
        return false;
    }

    const isInstanceKeyEqual = row.InstanceKey === getInstanceKeyForRow(tableView, row);

    const isDateTimeEqual = isItemView()
        ? areItemsDateTimeEqual(tableQuery, getMailStore().items.get(rowId), row)
        : areConversationsDateTimeEqual(
              tableQuery,
              getTableViewConversationRelation(rowId, tableView),
              row
          );

    if (!isInstanceKeyEqual || !isDateTimeEqual) {
        // if instanceKey or dateTime are not equal, return false
        return false;
    }

    switch (sortBy.sortColumn) {
        case SortColumn.Date:
            return true;

        default:
            trace.trace.warn('isSortKeyEqual: sortColumn not supported ' + sortBy.sortColumn);
            return false;
    }
}

function getInstanceKeyForRow(tableView: TableView, row: MailListRowDataType) {
    const rowId = getConversationOrItemIdFromRow(row);
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            const { instanceKey } = getTableViewConversationRelation(rowId, tableView);
            return instanceKey;

        case ReactListViewType.Message:
            const mailStore = getMailStore();
            const { InstanceKey } = mailStore.items.get(rowId);
            return InstanceKey;

        default:
            trace.errorThatWillCauseAlert('listViewType is not defined in getInstanceKeyForRow');
            return null;
    }
}

function areConversationsDateTimeEqual(
    tableQuery: TableQuery,
    tableConversationRelation: TableViewConversationRelation,
    conversation: ConversationType
): boolean {
    // Note: we need to convert the timestamp into date objects for comparison
    // because we've seen issues where the server may return the time in UTC format.
    // We should be resilient on the client against such issues.
    if (shouldTableSortByRenewTime(tableQuery)) {
        return (
            new Date(tableConversationRelation.lastDeliveryOrRenewTimeStamp).getTime() ===
            new Date(conversation.LastDeliveryOrRenewTime).getTime()
        );
    } else {
        return (
            new Date(tableConversationRelation.lastDeliveryTimeStamp).getTime() ===
            new Date(conversation.LastDeliveryTime).getTime()
        );
    }
}

function areItemsDateTimeEqual(
    tableQuery: TableQuery,
    itemTarget: Item,
    itemReference: Item
): boolean {
    // Note: we need to convert the timestamp into date objects for comparison
    // because we've seen issues where the server may return the time in UTC format.
    // We should be resilient on the client against such issues.
    const isDateTimeReceivedEqual =
        new Date(itemTarget.DateTimeReceived).getTime() ===
        new Date(itemReference.DateTimeReceived).getTime();

    if (shouldTableSortByRenewTime(tableQuery)) {
        const isReceivedOrRenewTimeEqual =
            new Date(itemTarget.ReceivedOrRenewTime).getTime() ===
            new Date(itemReference.ReceivedOrRenewTime).getTime();
        return isReceivedOrRenewTimeEqual;
    }

    return isDateTimeReceivedEqual;
}
